import { ChatBulkAuditBatch, SubtaskAssignmentAbbreviatedResponse } from '../SpeedAudit/types';
import {
  AttemptAuditMaxClaimTimeResponse,
  AuditViewType,
} from '@scale/shared/types/corp/attemptAudit';
import { createChatBulkAudit, createChatBulkAuditForContributors } from './ChatBulkAuditApi';
import { hrefMapping } from './ChatBulkAuditResultsPage';
import {
  hrefMapping as externalHrefMapping,
  ExternalAuditViewType,
} from './ExternalChatBulkAuditsTableView';
import { TaskType } from '@scale/shared-types';

export const defaultStaleTime = 60 * 60 * 1000; // 1 hour

export const getTimeWorkedText = (timeSpentSecs: number | undefined) => {
  if (timeSpentSecs === undefined) {
    return 'no time';
  }

  const hours = Math.floor(timeSpentSecs / 3600);
  const minutes = Math.floor((timeSpentSecs - hours * 3600) / 60);

  const hoursText = hours > 0 ? `${hours}h ` : '';
  return `${hoursText}${minutes}m`;
};

export const getCorrespondingAttemptAuditForContributorFeedback = (
  subtaskAssignments: SubtaskAssignmentAbbreviatedResponse[],
  contributorId: string,
): SubtaskAssignmentAbbreviatedResponse | null => {
  const attemptAudit = subtaskAssignments.find(
    subtaskAssignment => subtaskAssignment.attemptTaskerId?.toString() === contributorId,
  );
  if (!attemptAudit) {
    return null;
  }
  return attemptAudit;
};

export const getAttemptAuditIdForContributorFeedback = (
  auditBatch: ChatBulkAuditBatch,
  subtaskAssignments: SubtaskAssignmentAbbreviatedResponse[],
  contributorId: string,
): string | null => {
  const attemptAuditIdx = subtaskAssignments.findIndex(
    subtaskAssignment => subtaskAssignment.attemptTaskerId?.toString() === contributorId,
  );
  if (attemptAuditIdx === -1) {
    return null;
  }
  return auditBatch.attemptAudits[attemptAuditIdx];
};

export const getCurrentClaimTimeForReviewLevel = (
  claimTimes: AttemptAuditMaxClaimTimeResponse | undefined,
  reviewLevel: number | undefined,
): number | undefined => {
  if (claimTimes === undefined || reviewLevel === undefined) {
    return undefined;
  }

  switch (reviewLevel) {
    case -1:
      return claimTimes.claimTimeMaxHoldMinutesAttempt;
    case 0:
      return claimTimes.claimTimeMaxHoldMinutesL0;
    case 1:
      return claimTimes.claimTimeMaxHoldMinutesL1;
    case 4:
      return claimTimes.claimTimeMaxHoldMinutesReviewConsensus;
    case 8:
      return claimTimes.claimTimeMaxHoldMinutesL8;
    case 10:
      return claimTimes.claimTimeMaxHoldMinutesL10;
    default:
      return undefined;
  }
};

export const startChatBulkAuditForContributors = async ({
  projectId,
  workerIds,
  auditName,
  enableEditing,
  dateRange,
  limit,
  reviewLevels,
}: {
  projectId: string;
  workerIds: string[];
  auditName: string;
  enableEditing: boolean;
  dateRange: { startDate: Date; endDate: Date };
  limit: number;
  reviewLevels: string[];
}) => {
  const { auditBatch } = await createChatBulkAuditForContributors({
    projectId,
    workerIds,
    auditName,
    shouldClaimTasks: enableEditing,
    dateRange,
    limit,
    reviewLevels,
  });

  if (window.location.pathname.includes('corp')) {
    window.open(`/corp/chat_bulk_audit/${auditBatch._id}`, '_blank');
  } else if (window.location.pathname.includes('outlieradmin')) {
    window.open(`/expert/outlieradmin/tools/chat_bulk_audit/${auditBatch._id}`, '_blank');
  } else if (window.location.pathname.includes('remoadmin')) {
    window.open(`/remoadmin/tools/chat_bulk_audit/${auditBatch._id}`, '_blank');
  } else {
    const auditUrl = new URL(window.location.href);
    auditUrl.searchParams.set('auditBatchIdChatBulkAudit', auditBatch._id);
    window.open(auditUrl, '_blank');
  }
};

export const startChatBulkAudit = async ({
  projectId,
  auditName,
  taskIds,
  enableEditing = false,
}: {
  projectId: string;
  auditName: string;
  taskIds: string[];
  enableEditing?: boolean;
}) => {
  const { auditBatch } = await createChatBulkAudit({
    projectId,
    auditName,
    taskIds,
    shouldClaimTasks: enableEditing,
  });

  if (window.location.pathname.includes('corp')) {
    window.open(`/corp/chat_bulk_audit/${auditBatch._id}`, '_blank');
  } else if (window.location.pathname.includes('outlieradmin')) {
    window.open(`/expert/outlieradmin/tools/chat_bulk_audit/${auditBatch._id}`, '_blank');
  } else if (window.location.pathname.includes('remoadmin')) {
    window.open(`/remoadmin/tools/chat_bulk_audit/${auditBatch._id}`, '_blank');
  } else {
    const auditUrl = new URL(window.location.href);
    auditUrl.searchParams.set('auditBatchIdChatBulkAudit', auditBatch._id);
    window.open(auditUrl, '_blank');
  }
};

export const getAuditPageReturnUrl = (href: string, auditBatchId: string): string | undefined => {
  if (window.location.pathname.includes('corp')) {
    return `${href}${auditBatchId}?skipRedirect=true`;
  }
  const auditViewType = getAuditViewTypeFromHref(href);
  const externalHref = auditViewType
    ? externalHrefMapping[auditViewType.toString() as ExternalAuditViewType]
    : undefined;
  if (!externalHref) {
    return undefined;
  }
  if (window.location.pathname.includes('outlieradmin/tools/chat_bulk_audit')) {
    return `/expert/outlieradmin/tools/chat_bulk_audit/${auditBatchId}?skipRedirect=true`;
  }
  if (window.location.pathname.includes('remoadmin/tools/chat_bulk_audit')) {
    return `/remoadmin/tools/chat_bulk_audit/${auditBatchId}?skipRedirect=true`;
  }
  return undefined;
};

export const getAuditResultPageUrl = (auditBatchId: string): string | undefined => {
  if (window.location.pathname.includes('corp')) {
    return `/corp/chat_bulk_audit/${auditBatchId}/results`;
  }
  if (window.location.pathname.includes('outlieradmin/tools/chat_bulk_audit')) {
    return `/expert/outlieradmin/tools/chat_bulk_audit/${auditBatchId}/results`;
  }
  if (window.location.pathname.includes('remoadmin/tools/chat_bulk_audit')) {
    return `/remoadmin/tools/chat_bulk_audit/${auditBatchId}/results`;
  }
  return undefined;
};

export const isRemoOrOutlierAdmin = (): boolean => {
  return (
    window.location.pathname.includes('remoadmin') ||
    window.location.pathname.includes('outlieradmin')
  );
};

export const isChatExperimentalTaskType = (taskType: TaskType | undefined): boolean => {
  return taskType === TaskType.ChatExperimental || taskType === TaskType.ChatV2;
};

const getAuditViewTypeFromHref = (href: string): AuditViewType | undefined => {
  const entry = Object.entries(hrefMapping).find(([_, value]) => href.includes(value));
  return entry ? (entry[0] as AuditViewType) : undefined;
};
